import {
  Badge,
  Button,
  Checkbox,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft, IconCheck, IconRefresh } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { RequiredSkillsEditor } from "../../components/RequiredSkillsEditor";
import { JobFactCard, JOB_FACT_GRID_COLS } from "../../components/jobs/JobFactCard";
import { JobRequiredSkills } from "../../components/jobs/JobRequiredSkills";
import { ErrorState, LoadingOverlay, PageHeader, SectionBlock, StatusBadge } from "../../components/ui";
import { adminApi, contractsApi, jobsApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { WORK_MODE_LABELS, workModeFromJob } from "../../lib/formCatalogs";
import { formatDate, formatJobExperienceRange, formatPaise, SEGMENT_LABELS } from "../../lib/format";
import { NAV_ICONS } from "../../lib/navCopy";
import { axioTokens } from "../../theme";
import type { AdminJob, RequiredSkill } from "../../types/api";
import { AdminJobRecruiterPreview } from "./AdminJobRecruiterPreview";
import { JobInsightsPanel } from "./JobInsightsPanel";

const FEE_SPLIT_TOTAL = 100;
const DEFAULT_SOURCING_PCT = 10;
const DEFAULT_RECRUITER_PCT = 50;
const DEFAULT_PLATFORM_PCT = 40;
const DEFAULT_CONSULTANCY_SOURCING_PCT = 10;
const DEFAULT_CONSULTANCY_RECRUITER_PCT = 80;
const DEFAULT_CONSULTANCY_PLATFORM_PCT = 10;
const EMPTY_FACT = "—";
const CITY_SEPARATOR = ", ";
const BACK_LABEL = "Jobs";
const SAVE_SKILLS_LABEL = "Save skills";
const APPROVE_LABEL = "Approve & open job";
const BACKFILL_LABEL = "Backfill matches";
const ICON_SIZE = "var(--axio-unit-16)";
const BACK_ICON_SIZE = "var(--axio-unit-14)";

const VIEW_ADMIN = "admin";
const VIEW_PREVIEW = "preview";

const PAGE_COPY = {
  viewAdmin: "Admin",
  viewPreview: "Recruiter preview",
  approveTitle: "Approve fee split",
  approveDescription: `Splits must total ${FEE_SPLIT_TOTAL}%. Approving opens this job to recruiters.`,
  rateCard: "Rate card",
  redactDescription: "Redacted description (unverified recruiters)",
  redactRequirements: "Redacted requirements",
  originalDescription: "Original description",
  originalRequirements: "Original requirements",
  redactHint: 'Company names should read as "the hiring company".',
  consultancyOverride: "Override consultancy split for this job",
  skillsTitle: "Skills",
  skillsDescription: "Must-have vs preferred badges match the recruiter job page. Saving updates Explore ranking.",
  insightsTitle: "Insights",
  insightsDescription: "Write, generate, and publish notes. Published banners appear on the recruiter job page.",
  factsTitle: "Job facts",
  openings: "Openings",
  fee: "Fee",
  split: "Split",
  ctc: "CTC band",
  experience: "Experience",
  cities: "Cities",
  segment: "Segment",
  posted: "Posted",
  workMode: "Work mode",
  guarantee: "Guarantee window",
  daysSuffix: "days",
  noSplit: "No split yet",
  independentTotal: "Independent total",
  consultancyTotal: "Consultancy total",
  mustEqual: "must equal",
  sourcingPct: "Sourcing %",
  recruiterPct: "Recruiter %",
  platformPct: "Platform %",
  consultancySourcingPct: "Consultancy sourcing %",
  consultancyRecruiterPct: "Consultancy recruiter %",
  consultancyPlatformPct: "Consultancy platform %",
  termsPrefix: "Terms:",
  defaultSuffix: "(default)",
  loadingContracts: "Loading…",
  selectRateCard: "Select rate card",
  sourceBadge: "Source",
  recruiterBadge: "Recruiter",
  platformBadge: "Platform",
  consultancyBadge: "Consultancy",
} as const;

const PAGE_SECTION_GAP = axioTokens.unit[40];
const JOB_DETAIL_VIEWS = [
  { value: VIEW_ADMIN, label: PAGE_COPY.viewAdmin },
  { value: VIEW_PREVIEW, label: PAGE_COPY.viewPreview },
] as const;
const SPLIT_OK_MARK = "✓";
const REDACT_GRID_COLS = {
  base: 1,
  [axioTokens.containerQuery.sm]: 2,
} as const;

function citiesFact(job: AdminJob): string {
  const list = job.cities.map((city) => city.trim()).filter(Boolean);
  return list.length ? list.join(CITY_SEPARATOR) : EMPTY_FACT;
}

function ctcBand(job: AdminJob): string {
  if (job.ctc_min_paise == null && job.ctc_max_paise == null) return EMPTY_FACT;
  return `${formatPaise(job.ctc_min_paise)} – ${formatPaise(job.ctc_max_paise)}`;
}

function feeLabel(job: AdminJob): string {
  if (job.fee_basis === "pct_ctc") return `${job.fee_pct_ctc ?? EMPTY_FACT}% CTC`;
  return formatPaise(job.fee_flat_paise);
}

function guaranteeLabel(job: AdminJob): string {
  return job.guarantee_days != null ? `${job.guarantee_days} ${PAGE_COPY.daysSuffix}` : EMPTY_FACT;
}

function FeeSplitBadges(
  job: Pick<
    AdminJob,
    | "sourcing_fee_pct"
    | "recruiter_fee_pct"
    | "platform_fee_pct"
    | "consultancy_sourcing_fee_pct"
    | "consultancy_recruiter_fee_pct"
    | "consultancy_platform_fee_pct"
  >,
) {
  if (job.sourcing_fee_pct == null && job.recruiter_fee_pct == null && job.platform_fee_pct == null) {
    return (
      <Text size="sm" c="dimmed">
        {PAGE_COPY.noSplit}
      </Text>
    );
  }
  return (
    <Group gap="xs" wrap="wrap">
      <Badge variant="outline" color="teal">
        {PAGE_COPY.sourceBadge} {job.sourcing_fee_pct ?? EMPTY_FACT}%
      </Badge>
      <Badge variant="outline" color="violet">
        {PAGE_COPY.recruiterBadge} {job.recruiter_fee_pct ?? EMPTY_FACT}%
      </Badge>
      <Badge variant="outline" color="gray">
        {PAGE_COPY.platformBadge} {job.platform_fee_pct ?? EMPTY_FACT}%
      </Badge>
      {(job.consultancy_recruiter_fee_pct != null ||
        job.consultancy_sourcing_fee_pct != null ||
        job.consultancy_platform_fee_pct != null) && (
        <Badge variant="light" color="violet">
          {PAGE_COPY.consultancyBadge} {job.consultancy_recruiter_fee_pct ?? EMPTY_FACT}/
          {job.consultancy_sourcing_fee_pct ?? EMPTY_FACT}/{job.consultancy_platform_fee_pct ?? EMPTY_FACT}
        </Badge>
      )}
    </Group>
  );
}

export function AdminJobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const queryClient = useQueryClient();
  const [skills, setSkills] = useState<RequiredSkill[]>([]);
  const [approveContractId, setApproveContractId] = useState<string | null>(null);
  const [sourcingPct, setSourcingPct] = useState<number | string>(DEFAULT_SOURCING_PCT);
  const [recruiterPct, setRecruiterPct] = useState<number | string>(DEFAULT_RECRUITER_PCT);
  const [platformPct, setPlatformPct] = useState<number | string>(DEFAULT_PLATFORM_PCT);
  const [consultancySourcingPct, setConsultancySourcingPct] = useState<number | string>(
    DEFAULT_CONSULTANCY_SOURCING_PCT,
  );
  const [consultancyRecruiterPct, setConsultancyRecruiterPct] = useState<number | string>(
    DEFAULT_CONSULTANCY_RECRUITER_PCT,
  );
  const [consultancyPlatformPct, setConsultancyPlatformPct] = useState<number | string>(
    DEFAULT_CONSULTANCY_PLATFORM_PCT,
  );
  const [overrideConsultancy, setOverrideConsultancy] = useState(false);
  const [redactedDescription, setRedactedDescription] = useState("");
  const [redactedRequirements, setRedactedRequirements] = useState("");
  const [view, setView] = useState<typeof VIEW_ADMIN | typeof VIEW_PREVIEW>(VIEW_ADMIN);

  const jobQuery = useQuery({
    queryKey: ["admin-job", jobId],
    queryFn: () => adminApi.getJob(jobId!),
    enabled: !!jobId,
  });

  const job = jobQuery.data;

  const insightsQuery = useQuery({
    queryKey: ["admin-job-insights", jobId],
    queryFn: () => adminApi.listJobInsights(jobId!),
    enabled: !!jobId,
  });

  const approveContractsQuery = useQuery({
    queryKey: ["admin-approve-contracts", job?.company_id],
    queryFn: () => contractsApi.listCompanyContracts(job!.company_id),
    enabled: !!job?.company_id && job.status === "pending_review",
  });

  useEffect(() => {
    if (!job) return;
    setSkills(
      (job.required_skills ?? []).map((skill) => ({
        skill: skill.skill,
        must_have: !!skill.must_have,
      })),
    );
    setApproveContractId(job.company_contract_id ?? null);
    setSourcingPct(job.sourcing_fee_pct ?? DEFAULT_SOURCING_PCT);
    setRecruiterPct(job.recruiter_fee_pct ?? DEFAULT_RECRUITER_PCT);
    setPlatformPct(job.platform_fee_pct ?? DEFAULT_PLATFORM_PCT);
    const hasConsultancyOverride =
      job.consultancy_sourcing_fee_pct != null ||
      job.consultancy_recruiter_fee_pct != null ||
      job.consultancy_platform_fee_pct != null;
    setOverrideConsultancy(hasConsultancyOverride);
    setConsultancySourcingPct(job.consultancy_sourcing_fee_pct ?? DEFAULT_CONSULTANCY_SOURCING_PCT);
    setConsultancyRecruiterPct(job.consultancy_recruiter_fee_pct ?? DEFAULT_CONSULTANCY_RECRUITER_PCT);
    setConsultancyPlatformPct(job.consultancy_platform_fee_pct ?? DEFAULT_CONSULTANCY_PLATFORM_PCT);
    setRedactedDescription(job.redacted_description ?? job.description ?? "");
    setRedactedRequirements(job.redacted_requirements ?? job.requirements ?? "");
  }, [job]);

  const activeApproveContracts = useMemo(
    () => (approveContractsQuery.data ?? []).filter((contract) => contract.status === "active"),
    [approveContractsQuery.data],
  );
  const preferredApproveContract =
    activeApproveContracts.find((contract) => contract.id === job?.company_contract_id) ??
    activeApproveContracts.find((contract) => contract.is_default) ??
    activeApproveContracts[0] ??
    null;
  const effectiveApproveContractId = approveContractId ?? preferredApproveContract?.id ?? null;
  const selectedApproveContract =
    activeApproveContracts.find((contract) => contract.id === effectiveApproveContractId) ?? null;

  const splitSum = useMemo(() => {
    return (Number(sourcingPct) || 0) + (Number(recruiterPct) || 0) + (Number(platformPct) || 0);
  }, [sourcingPct, recruiterPct, platformPct]);

  const consultancySplitSum = useMemo(() => {
    return (
      (Number(consultancySourcingPct) || 0) +
      (Number(consultancyRecruiterPct) || 0) +
      (Number(consultancyPlatformPct) || 0)
    );
  }, [consultancySourcingPct, consultancyRecruiterPct, consultancyPlatformPct]);

  const publishedInsights = useMemo(
    () => (insightsQuery.data ?? []).filter((insight) => insight.published),
    [insightsQuery.data],
  );

  const saveSkillsMutation = useMutation({
    mutationFn: () => jobsApi.update(jobId!, { required_skills: skills }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Job skills updated" });
      void queryClient.invalidateQueries({ queryKey: ["admin-job", jobId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-jobs"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Failed to update skills",
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: () =>
      adminApi.approveJobFees(jobId!, {
        sourcing_fee_pct: Number(sourcingPct),
        recruiter_fee_pct: Number(recruiterPct),
        platform_fee_pct: Number(platformPct),
        company_contract_id: effectiveApproveContractId ?? undefined,
        ...(overrideConsultancy
          ? {
              consultancy_sourcing_fee_pct: Number(consultancySourcingPct),
              consultancy_recruiter_fee_pct: Number(consultancyRecruiterPct),
              consultancy_platform_fee_pct: Number(consultancyPlatformPct),
            }
          : {}),
        redacted_description: redactedDescription,
        redacted_requirements: redactedRequirements,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Fee split approved — job is now open" });
      void queryClient.invalidateQueries({ queryKey: ["admin-job", jobId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-jobs"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Failed to approve fees",
      });
    },
  });

  const backfillMutation = useMutation({
    mutationFn: () => adminApi.backfillJob(jobId!),
    onSuccess: (res) => {
      notifications.show({
        color: "green",
        message:
          res.created > 0
            ? `Backfilled ${res.created} new match(es) for this job`
            : "No new matches — pool already covered for this job",
      });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Backfill failed",
      });
    },
  });

  if (jobQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-280)" />;
  }
  if (jobQuery.isError) {
    return (
      <ErrorState
        message={jobQuery.error instanceof Error ? jobQuery.error.message : "Could not load this job."}
        onRetry={() => void jobQuery.refetch()}
      />
    );
  }
  if (!job || !jobId) {
    return <ErrorState title="Job not found" message="This req may have been removed." />;
  }

  const pendingReview = job.status === "pending_review";
  const canBackfill = job.status === "open";
  const experience = formatJobExperienceRange(job.experience_min, job.experience_max);
  const workMode = WORK_MODE_LABELS[workModeFromJob(job.work_mode, job.is_remote)];
  const approveDisabled =
    splitSum !== FEE_SPLIT_TOTAL ||
    !effectiveApproveContractId ||
    (overrideConsultancy && consultancySplitSum !== FEE_SPLIT_TOTAL);

  return (
    <Stack gap={PAGE_SECTION_GAP}>
      <PageHeader
        icon={NAV_ICONS.jobs}
        title={job.title}
        subtitle={job.company_name}
        breadcrumb={
          <Button
            component={Link}
            to="/admin/jobs"
            variant="subtle"
            size="compact-sm"
            w="fit-content"
            leftSection={<IconArrowLeft size={BACK_ICON_SIZE} />}
          >
            {BACK_LABEL}
          </Button>
        }
        actions={
          <>
            <StatusBadge status={job.status} />
            {canBackfill ? (
              <Button
                variant="default"
                leftSection={<IconRefresh size={ICON_SIZE} />}
                loading={backfillMutation.isPending}
                onClick={() => backfillMutation.mutate()}
              >
                {BACKFILL_LABEL}
              </Button>
            ) : null}
          </>
        }
      />

      <SegmentedControl
        value={view}
        onChange={(value) => {
          if (value === VIEW_ADMIN || value === VIEW_PREVIEW) setView(value);
        }}
        data={[...JOB_DETAIL_VIEWS]}
      />

      {view === VIEW_PREVIEW ? (
        <AdminJobRecruiterPreview job={job} publishedInsights={publishedInsights} />
      ) : (
        <Stack gap={PAGE_SECTION_GAP}>
          <SectionBlock title={PAGE_COPY.factsTitle}>
            <Stack gap="lg">
              <SimpleGrid type="container" cols={JOB_FACT_GRID_COLS} spacing="lg">
                <JobFactCard label={PAGE_COPY.ctc} tabular>
                  {ctcBand(job)}
                </JobFactCard>
                <JobFactCard label={PAGE_COPY.experience}>{experience ?? EMPTY_FACT}</JobFactCard>
                <JobFactCard label={PAGE_COPY.openings} tabular>
                  {job.openings_filled}/{job.openings_total}
                </JobFactCard>
                <JobFactCard label={PAGE_COPY.cities}>{citiesFact(job)}</JobFactCard>
                <JobFactCard label={PAGE_COPY.segment}>{SEGMENT_LABELS[job.segment] ?? job.segment}</JobFactCard>
                <JobFactCard label={PAGE_COPY.posted} tabular>
                  {formatDate(job.published_at ?? job.created_at)}
                </JobFactCard>
                <JobFactCard label={PAGE_COPY.workMode}>{workMode}</JobFactCard>
                <JobFactCard label={PAGE_COPY.guarantee} tabular>
                  {guaranteeLabel(job)}
                </JobFactCard>
                <JobFactCard label={PAGE_COPY.fee} tabular>
                  {feeLabel(job)}
                </JobFactCard>
                <JobFactCard label={PAGE_COPY.split}>
                  <FeeSplitBadges {...job} />
                </JobFactCard>
              </SimpleGrid>
              <JobRequiredSkills skills={job.required_skills} />
            </Stack>
          </SectionBlock>

          {pendingReview ? (
            <SectionBlock title={PAGE_COPY.approveTitle} description={PAGE_COPY.approveDescription}>
              <Stack gap="md">
                <Select
                  label={PAGE_COPY.rateCard}
                  placeholder={approveContractsQuery.isLoading ? PAGE_COPY.loadingContracts : PAGE_COPY.selectRateCard}
                  data={activeApproveContracts.map((contract) => ({
                    value: contract.id,
                    label: `${contract.name}${contract.is_default ? ` ${PAGE_COPY.defaultSuffix}` : ""} — ${
                      contract.fee_basis === "pct_ctc"
                        ? `${contract.fee_pct_ctc ?? EMPTY_FACT}% CTC`
                        : formatPaise(contract.fee_flat_paise)
                    }`,
                  }))}
                  value={effectiveApproveContractId}
                  onChange={setApproveContractId}
                  searchable
                  required
                />
                {selectedApproveContract && (
                  <Text size="sm" c="dimmed">
                    {PAGE_COPY.termsPrefix}{" "}
                    {selectedApproveContract.fee_basis === "pct_ctc"
                      ? `${selectedApproveContract.fee_pct_ctc ?? EMPTY_FACT}% CTC`
                      : formatPaise(selectedApproveContract.fee_flat_paise)}
                    {" · "}
                    {selectedApproveContract.guarantee_days} {PAGE_COPY.daysSuffix} guarantee
                  </Text>
                )}
                <SimpleGrid type="container" cols={JOB_FACT_GRID_COLS} spacing="lg">
                  <NumberInput
                    label={PAGE_COPY.sourcingPct}
                    min={0}
                    max={FEE_SPLIT_TOTAL}
                    value={sourcingPct}
                    onChange={setSourcingPct}
                  />
                  <NumberInput
                    label={PAGE_COPY.recruiterPct}
                    min={0}
                    max={FEE_SPLIT_TOTAL}
                    value={recruiterPct}
                    onChange={setRecruiterPct}
                  />
                  <NumberInput
                    label={PAGE_COPY.platformPct}
                    min={0}
                    max={FEE_SPLIT_TOTAL}
                    value={platformPct}
                    onChange={setPlatformPct}
                  />
                </SimpleGrid>
                <Text size="sm" c={splitSum === FEE_SPLIT_TOTAL ? "teal" : "red"} fw="var(--axio-font-weight-semibold)">
                  {PAGE_COPY.independentTotal}: {splitSum}%{" "}
                  {splitSum === FEE_SPLIT_TOTAL ? SPLIT_OK_MARK : `(${PAGE_COPY.mustEqual} ${FEE_SPLIT_TOTAL})`}
                </Text>
                <SimpleGrid type="container" cols={REDACT_GRID_COLS} spacing="lg">
                  <Textarea label={PAGE_COPY.originalDescription} minRows={6} readOnly value={job.description ?? ""} />
                  <Textarea
                    label={PAGE_COPY.redactDescription}
                    description={PAGE_COPY.redactHint}
                    minRows={6}
                    value={redactedDescription}
                    onChange={(e) => setRedactedDescription(e.currentTarget.value)}
                  />
                  <Textarea label={PAGE_COPY.originalRequirements} minRows={5} readOnly value={job.requirements ?? ""} />
                  <Textarea
                    label={PAGE_COPY.redactRequirements}
                    minRows={5}
                    value={redactedRequirements}
                    onChange={(e) => setRedactedRequirements(e.currentTarget.value)}
                  />
                </SimpleGrid>
                <Checkbox
                  label={PAGE_COPY.consultancyOverride}
                  checked={overrideConsultancy}
                  onChange={(e) => setOverrideConsultancy(e.currentTarget.checked)}
                />
                {overrideConsultancy ? (
                  <Stack gap="md">
                    <SimpleGrid type="container" cols={JOB_FACT_GRID_COLS} spacing="lg">
                      <NumberInput
                        label={PAGE_COPY.consultancySourcingPct}
                        min={0}
                        max={FEE_SPLIT_TOTAL}
                        value={consultancySourcingPct}
                        onChange={setConsultancySourcingPct}
                      />
                      <NumberInput
                        label={PAGE_COPY.consultancyRecruiterPct}
                        min={0}
                        max={FEE_SPLIT_TOTAL}
                        value={consultancyRecruiterPct}
                        onChange={setConsultancyRecruiterPct}
                      />
                      <NumberInput
                        label={PAGE_COPY.consultancyPlatformPct}
                        min={0}
                        max={FEE_SPLIT_TOTAL}
                        value={consultancyPlatformPct}
                        onChange={setConsultancyPlatformPct}
                      />
                    </SimpleGrid>
                    <Text
                      size="sm"
                      c={consultancySplitSum === FEE_SPLIT_TOTAL ? "teal" : "red"}
                      fw="var(--axio-font-weight-semibold)"
                    >
                      {PAGE_COPY.consultancyTotal}: {consultancySplitSum}%{" "}
                      {consultancySplitSum === FEE_SPLIT_TOTAL
                        ? SPLIT_OK_MARK
                        : `(${PAGE_COPY.mustEqual} ${FEE_SPLIT_TOTAL})`}
                    </Text>
                  </Stack>
                ) : null}
                <Group>
                  <Button
                    color="teal"
                    leftSection={<IconCheck size={ICON_SIZE} />}
                    disabled={approveDisabled}
                    loading={approveMutation.isPending}
                    onClick={() => approveMutation.mutate()}
                  >
                    {APPROVE_LABEL}
                  </Button>
                </Group>
              </Stack>
            </SectionBlock>
          ) : null}

          <SectionBlock title={PAGE_COPY.skillsTitle} description={PAGE_COPY.skillsDescription}>
            <Stack gap="md">
              <RequiredSkillsEditor value={skills} onChange={setSkills} />
              <Group>
                <Button loading={saveSkillsMutation.isPending} onClick={() => saveSkillsMutation.mutate()}>
                  {SAVE_SKILLS_LABEL}
                </Button>
              </Group>
            </Stack>
          </SectionBlock>

          <SectionBlock title={PAGE_COPY.insightsTitle} description={PAGE_COPY.insightsDescription}>
            <JobInsightsPanel jobId={jobId} />
          </SectionBlock>
        </Stack>
      )}
    </Stack>
  );
}
